// src/components/auth/LoginModal.jsx
import React, { useState, useContext } from "react";
import { supabase } from "../../supabaseClient";
import { AuthContext } from "../../contexts/AuthContext";
import { Link } from "react-router-dom";
import PasswordResetModal from "./PasswordResetModal";

export default function LoginModal() {
  const [isSignUp, setIsSignUp] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { loading } = useContext(AuthContext);
  const [showResetModal, setShowResetModal] = useState(false);

  const toggleMode = () => {
    setErrorMessage("");
    setIsSignUp(!isSignUp);
  };

  const resetForm = () => {
    setName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setErrorMessage("");
    setIsSignUp(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }

    // Password complexity validation
    if (password.length < 8) {
      setErrorMessage("Password must be at least 8 characters long.");
      return;
    }

    // Check for weak passwords
    const weakPasswords = new Set([
      "123456",
      "12345678",
      "123456789",
      "123azerty",
      "password",
      "qwerty",
      "abc123",
      "111111",
    ]);
    if (weakPasswords.has(password.toLowerCase())) {
      setErrorMessage("Please choose a stronger password.");
      return;
    }

    if (isSignUp) {
      if (password !== confirmPassword) {
        setErrorMessage("Passwords do not match.");
        return;
      }
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { display_name: name } },
      });
      if (error) {
        setErrorMessage(error.message);
        return;
      }
      setShowSuccessModal(true);
    } else {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        setErrorMessage(error.message);
        return;
      }
    }
  };

  const SuccessModal = () => (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white w-full max-w-sm p-6 rounded-lg shadow-lg text-center">
        <div className="flex justify-center mb-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-12 w-12 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2l4-4" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10s-4.477 10-10 10z" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold mb-2">Account Created!</h3>
        <p className="text-gray-700">
          You will receive an email shortly with instructions to verify your account.
        </p>
        <button
          onClick={() => {
            setShowSuccessModal(false);
            resetForm();
          }}
          className="mt-4 px-4 py-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );

  if (showSuccessModal) {
    return <SuccessModal />;
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white w-full max-w-md p-6 rounded-lg shadow-lg relative">
        <h2 className="text-xl font-semibold mb-4">
          {isSignUp ? "Create an Account" : "Sign In"}
        </h2>
        {errorMessage && (
          <div className="mb-4 flex items-center bg-red-100 border border-red-300 text-red-700 text-sm font-medium rounded p-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4m0 4h.01M12 2a10 10 0 100 20a10 10 0 000-20z"
              />
            </svg>
            <span>{errorMessage}</span>
          </div>
        )}
        <form onSubmit={handleSubmit} autoComplete="on" className="space-y-4">
          {isSignUp && (
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                autoComplete="name"
                className="border border-green-200 w-full rounded px-3 py-2 focus:ring-2 focus:ring-green-400"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
          )}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              className="border border-green-200 w-full rounded px-3 py-2 focus:ring-2 focus:ring-green-400"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete={isSignUp ? "new-password" : "current-password"}
              className="border border-green-200 w-full rounded px-3 py-2 focus:ring-2 focus:ring-green-400"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          {isSignUp && (
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                className="border border-green-200 w-full rounded px-3 py-2 focus:ring-2 focus:ring-green-400"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors"
          >
            {isSignUp ? "Sign Up" : "Login"}
          </button>
        </form>
        <div className="mt-4 text-center">
          {!isSignUp && (
            <button
              onClick={() => setShowResetModal(true)}
              className="text-green-600 hover:underline text-sm"
            >
              Forgot your password?
            </button>
          )}
        </div>
        <div className="mt-4 text-center">
          <button className="text-green-600 hover:underline" onClick={toggleMode}>
            {isSignUp ? "Already have an account? Sign in" : "No account yet? Create one"}
          </button>
        </div>
      </div>
      {showResetModal && <PasswordResetModal onClose={() => setShowResetModal(false)} />}
    </div>
  );
}
